import moment from "moment";
import React, { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../api/APIContext";
import type { Company, Project } from "../../api/dto";
import type { Inspection } from "../../api/dto/Inspection";
import type { Question } from "../../api/dto/Question";
import type { QuestionSet } from "../../api/dto/QuestionSet";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";
import { SectionHeader } from "../../components/content/SectionHeader";
import { BarGraph } from "../../components/dashboards/BarGraph";
import { WorkstationInspectionCalculator } from "../../components/dashboards/calculation/WorkstationInspectionCalculator";
import { DashboardCard } from "../../components/dashboards/DashboardCard";
import { DashboardMetricTile } from "../../components/dashboards/DashboardMetricTile";
import { TrendArrow } from "../../components/dashboards/TrendArrow";
import { Autocomplete } from "../../components/form/Autocomplete";
import { Input } from "../../components/form/Input";
import { MultiAutocomplete } from "../../components/form/MultiAutocomplete";
import { InspectionFindingSeverityBadge } from "../../components/inspections/InspectionFindingSeverityBadge";
import { Paragraph } from "../../components/Paragraph";
import { questionLabel } from "../../util/questionLabel";
import { usePersistentState } from "../../util/usePersistentState";

// `childCompanies` is only populated one level deep by the API, so descendants are found
// by walking the flat `companies` list rather than trusting nested `childCompanies` entries.
const getCompanyWithDescendants = (root: Company, allCompanies: Company[]): Company[] => {
  const byId = new Map(allCompanies.map((c) => [c.id, c] as const));
  const visited = new Set<string>([root.id]);
  const result: Company[] = [];
  const queue: Company[] = [byId.get(root.id) ?? root];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) break;
    result.push(current);
    for (const child of current.childCompanies) {
      if (!visited.has(child.id)) {
        visited.add(child.id);
        queue.push(byId.get(child.id) ?? child);
      }
    }
  }
  return result;
};

const getHijsfirmas = (inspection: Inspection): { key: string; label: string }[] =>
  (inspection.extraFieldsFormatted.Hijsfirma ?? "")
    .split(",")
    .map((n) => n.trim())
    .filter((n) => n !== "")
    .map((label) => ({ key: label.toUpperCase(), label }));

const getAllHijsfirmas = (inspections: Inspection[]): { key: string; label: string }[] => {
  const labelByKey = new Map(
    inspections.flatMap(getHijsfirmas).map(({ key, label }) => [key, label]),
  );
  return Array.from(labelByKey, ([key, label]) => ({ key, label }));
};

const SCORE_BAR_STYLE_CONDITIONS = [
  { min: 0.0, max: 5.5, style: "red" as const },
  { min: 5.5, max: 6.0, style: "amber" as const },
  { min: 6.0, max: 10, style: "green" as const },
];

const buildAverageScoreBarData = <T,>(
  groups: T[],
  getGroupInspections: (group: T) => Inspection[],
  getLabel: (group: T) => string,
) =>
  groups
    .map((group) => {
      const groupInspections = getGroupInspections(group);
      if (groupInspections.length === 0) return null;
      const averageScore =
        groupInspections.reduce((c, i) => c + (i?.cachedResults?.score ?? 0), 0) /
        groupInspections.length;
      return { label: getLabel(group), value: averageScore, displayAs: averageScore.toFixed(2) };
    })
    .filter((x) => x !== null);

export const DashboardsWorkstationInspections: FC = () => {
  const { companies, projects, inspections, questionSets } = useApi();
  const [company, setCompany] = usePersistentState<Company["id"] | null>(
    "workstation-inspection-dashboard-company",
    null,
  );
  const [selectedProjectIds, setSelectedProjectIds] = usePersistentState<Project["id"][] | null>(
    "workstation-inspection-dashboard-project-v2",
    null,
  );
  const [selectedChildCompanyIds, setSelectedChildCompanyIds] = usePersistentState<string[]>(
    "workstation-inspection-dashboard-subcompanies",
    [],
  );
  const [selectedHijsfirmas, setSelectedHijsfirmas] = usePersistentState<string[] | null>(
    "workstation-inspection-dashboard-hijsfirmas",
    null,
  );
  const [from, setFrom] = useState<Date>(moment().subtract(90, "days").toDate());
  const [to, setTo] = useState<Date>(new Date());

  // Reset the sub-company selection whenever the organisation changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset on company change only
  useEffect(() => {
    setSelectedChildCompanyIds([]);
  }, [company]);

  // Project and hijsfirma options are scoped by which companies are in view (organisation +
  // selected sub-companies), so a stale, narrowed selection can silently exclude data that just
  // came into scope. Reset both back to "everything" whenever that scope changes (this also
  // fires when the organisation itself changes, since that resets selectedChildCompanyIds above).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset on scope change only
  useEffect(() => {
    setSelectedProjectIds(null);
    setSelectedHijsfirmas(null);
  }, [selectedChildCompanyIds]);

  const companyOptions = useMemo(() => {
    return Object.fromEntries([
      ["-", "- Selecteer bedrijf -"],
      ...companies.filter((c) => !c.isArchived).map((c) => [c.id, c.name]),
    ]);
  }, [companies]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === company),
    [companies, company],
  );
  const directChildCompanies = selectedCompany?.childCompanies ?? [];
  const includedDirectChildCompanies = directChildCompanies.filter((c) =>
    selectedChildCompanyIds.includes(c.id),
  );

  const implicitlySelectedCompanies = useMemo(() => {
    if (!selectedCompany) return [];
    return [
      selectedCompany,
      ...includedDirectChildCompanies.flatMap((c) => getCompanyWithDescendants(c, companies)),
    ];
  }, [selectedCompany, includedDirectChildCompanies, companies]);

  const implicitlySelectedCompanyIds = useMemo(
    () => new Set(implicitlySelectedCompanies.map((c) => c.id)),
    [implicitlySelectedCompanies],
  );

  const companyFilteredInspections = useMemo(() => {
    return inspections
      .filter((inspection) => inspection.cachedResults !== null)
      .filter((inspection) => implicitlySelectedCompanyIds.has(inspection.companyId));
  }, [inspections, implicitlySelectedCompanyIds]);

  // Not yet narrowed down to the selected project: used to determine which projects have
  // inspections in-range at all, regardless of which project happens to be selected.
  const periodInspections = useMemo(() => {
    return companyFilteredInspections
      .filter((inspection) => inspection.archivedAt === null)
      .filter(
        (inspection) =>
          inspection.inspectionAt !== null &&
          moment(inspection.inspectionAt).isBetween(from, to, "day", "[]"),
      );
  }, [companyFilteredInspections, from, to]);
  const previousPeriodInspections = useMemo(() => {
    return companyFilteredInspections.filter(
      (inspection) =>
        inspection.inspectionAt !== null &&
        moment(inspection.inspectionAt).isBetween(
          moment(from).subtract(moment(to).diff(from, "days"), "days"),
          from,
          "day",
          "[]",
        ),
    );
  }, [companyFilteredInspections, from, to]);

  const hijsfirmaOptions = useMemo(() => {
    return getAllHijsfirmas(periodInspections)
      .map(({ key, label }) => ({ value: key, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [periodInspections]);

  const projectIdsWithInspectionsInRange = useMemo(() => {
    return new Set([
      ...periodInspections.map((i) => i.projectId),
      ...previousPeriodInspections.map((i) => i.projectId),
    ]);
  }, [periodInspections, previousPeriodInspections]);

  const projectOptions = useMemo(() => {
    return Object.fromEntries(
      projects
        .filter((p) => implicitlySelectedCompanyIds.has(p.companyId) && !p.isArchived)
        .filter((p) => projectIdsWithInspectionsInRange.has(p.id))
        .map((p) => [p.id, p.name]),
    );
  }, [projects, implicitlySelectedCompanyIds, projectIdsWithInspectionsInRange]);

  // Note: `null` is correct for both — it means no filter was applied on that dimension.
  const matchesProjectAndHijsfirmaFilters = useCallback(
    (inspection: Inspection) =>
      (selectedProjectIds === null || selectedProjectIds.includes(inspection.projectId)) &&
      (selectedHijsfirmas === null ||
        getHijsfirmas(inspection).some(({ key }) => selectedHijsfirmas.includes(key))),
    [selectedProjectIds, selectedHijsfirmas],
  );

  const filteredInspections = useMemo(
    () => periodInspections.filter(matchesProjectAndHijsfirmaFilters),
    [periodInspections, matchesProjectAndHijsfirmaFilters],
  );
  const previousPeriodFilteredInspections = useMemo(
    () => previousPeriodInspections.filter(matchesProjectAndHijsfirmaFilters),
    [previousPeriodInspections, matchesProjectAndHijsfirmaFilters],
  );

  const groupProjects = useMemo(() => {
    return projects
      .filter((p) => implicitlySelectedCompanyIds.has(p.companyId) && !p.isArchived)
      .filter((p) => projectIdsWithInspectionsInRange.has(p.id))
      .filter((p) => selectedProjectIds === null || selectedProjectIds.includes(p.id));
  }, [
    projects,
    implicitlySelectedCompanyIds,
    projectIdsWithInspectionsInRange,
    selectedProjectIds,
  ]);

  const questions = useMemo(() => {
    return questionSets
      .flatMap((questionSet: QuestionSet) => questionSet.questions)
      .filter((q) => q.type !== "non_question");
  }, [questionSets]);

  return (
    <ContentContainer size={"lg"}>
      <div className={"flex items-center justify-between"}>
        <PageHeader>Werkplekinspecties Hijswerkzaamheden</PageHeader>
      </div>
      <div className={"flex flex-col gap-4 mt-4"}>
        <div className={"grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 pr-4"}>
          <div>
            <Autocomplete
              label={"Organisatie"}
              options={companyOptions}
              value={company === null ? "-" : company}
              onChange={(v) => setCompany(v === "-" ? null : v)}
            />
          </div>
          <MultiAutocomplete
            label={"Project"}
            options={projectOptions}
            value={selectedProjectIds ?? Object.keys(projectOptions)}
            onChange={setSelectedProjectIds}
            maxWidth={"full"}
          />
          {hijsfirmaOptions.length > 1 && (
            <MultiAutocomplete
              label={"Hijsbedrijven"}
              options={Object.fromEntries(hijsfirmaOptions.map((o) => [o.value, o.label]))}
              value={selectedHijsfirmas ?? hijsfirmaOptions.map((o) => o.value)}
              onChange={setSelectedHijsfirmas}
              maxWidth={"full"}
            />
          )}
        </div>
        <div className={"flex flex-row items-end justify-between gap-3 pr-4"}>
          {directChildCompanies.length > 0 && (
            <MultiAutocomplete
              label={"Subbedrijven"}
              options={Object.fromEntries(directChildCompanies.map((c) => [c.id, c.name]))}
              value={selectedChildCompanyIds}
              onChange={setSelectedChildCompanyIds}
            />
          )}
          <div className={"flex flex-row items-end space-x-3 ml-auto"}>
            <div>
              <Input label={"Van"} type={"date"} value={from} onChange={setFrom} />
            </div>
            <div>
              <Input label={"Tot"} type={"date"} value={to} onChange={setTo} />
            </div>
          </div>
        </div>
        {selectedCompany && filteredInspections.length > 0 ? (
          <WorkstationInspectionDashboard
            from={from}
            to={to}
            company={selectedCompany}
            questions={questions}
            projectsInCompany={groupProjects}
            companies={companies}
            inspections={filteredInspections}
            previousInspections={previousPeriodFilteredInspections}
            selectedHijsfirmas={selectedHijsfirmas}
          />
        ) : (
          <div className={"rounded-lg bg-slate-100 px-4 py-3"}>
            <SectionHeader>Geen resultaten</SectionHeader>
            <Paragraph>Pas de filters aan om het dashboard te bekijken</Paragraph>
          </div>
        )}
      </div>
    </ContentContainer>
  );
};

interface WorkstationInspectionDashboardProps {
  from: Date;
  to: Date;
  company: Company;
  projectsInCompany: Project[];
  companies: Company[];
  inspections: Inspection[];
  previousInspections: Inspection[];
  questions: Question[];
  selectedHijsfirmas: string[] | null;
}

const WorkstationInspectionDashboard: FC<WorkstationInspectionDashboardProps> = (props) => {
  const headTiles = useMemo(() => {
    return new WorkstationInspectionCalculator(
      props.previousInspections,
      props.inspections,
    ).getHeadTiles();
  }, [props.inspections, props.previousInspections]);
  const categoryTiles = useMemo(() => {
    return new WorkstationInspectionCalculator(
      props.previousInspections,
      props.inspections,
    ).getCategoryScoreTiles();
  }, [props.inspections, props.previousInspections]);

  const findingsTrend = useMemo(() => {
    return new WorkstationInspectionCalculator(
      props.previousInspections,
      props.inspections,
    ).getCriticalFindingsTrend();
  }, [props.inspections, props.previousInspections]);

  const positiveQuestions = useMemo(() => {
    return new WorkstationInspectionCalculator(
      props.previousInspections,
      props.inspections,
    ).getTopPositiveQuestions(props.questions);
  }, [props.inspections, props.previousInspections, props.questions]);

  const subCompanyIds = useMemo(
    () => Array.from(new Set(props.inspections.map((i) => i.companyId))),
    [props.inspections],
  );
  const hijsfirmas = useMemo(() => {
    const distinct = getAllHijsfirmas(props.inspections);
    const selected = props.selectedHijsfirmas;
    // Restrict to the selected firms: a matching inspection can also mention other, non-selected
    // firms, which would otherwise leak into this breakdown even when only one was picked.
    return selected === null ? distinct : distinct.filter((h) => selected.includes(h.key));
  }, [props.inspections, props.selectedHijsfirmas]);

  return (
    <div className={"pr-4 flex flex-col gap-4"}>
      <section className={"grid grid-cols-1 xl:grid-cols-5 gap-4"}>
        <DashboardMetricTile
          name={"Inspecties deze periode"}
          trendArrow={headTiles.inspectionsCount.trend}
          value={headTiles.inspectionsCount.formatCurrent}
          subText={
            headTiles.inspectionsCount.trend === null
              ? "hetzelfde t.o.v. vorige periode"
              : `${Math.abs(headTiles.inspectionsCount.delta).toFixed(0)} ${headTiles.inspectionsCount.trend === "up" ? "meer" : "minder"} t.o.v. vorige periode`
          }
          subTextStyle={
            headTiles.inspectionsCount.trend === null
              ? "amber"
              : headTiles.inspectionsCount.trend === "up"
                ? "green"
                : "red"
          }
        />
        <DashboardMetricTile
          name={"Gemiddelde score"}
          trendArrow={headTiles.averageInspectionScore.trend}
          value={headTiles.averageInspectionScore.formatCurrent}
          subText={
            headTiles.averageInspectionScore.trend === null
              ? "hetzelfde t.o.v. vorige periode"
              : `${Math.abs(headTiles.averageInspectionScore.delta).toFixed(1)} ${headTiles.averageInspectionScore.trend === "up" ? "meer" : "minder"} t.o.v. vorige periode`
          }
          subTextStyle={
            headTiles.averageInspectionScore.trend === null
              ? "amber"
              : headTiles.averageInspectionScore.trend === "up"
                ? "green"
                : "red"
          }
        />
        <DashboardMetricTile
          name={"L.S.R. Overtredingen"}
          trendArrow={headTiles.lsrFindings.trend}
          inverseTrendArrowStyle={true}
          value={headTiles.lsrFindings.formatCurrent}
          subText={
            headTiles.lsrFindings.trend === null
              ? "hetzelfde t.o.v. vorige periode"
              : `${Math.abs(headTiles.lsrFindings.delta).toFixed(0)} ${headTiles.inspectionsCount.trend === "up" ? "meer" : "minder"} t.o.v. vorige periode`
          }
          subTextStyle={
            headTiles.lsrFindings.trend === null
              ? "amber"
              : headTiles.lsrFindings.trend === "up"
                ? "red"
                : "green"
          }
        />
        <DashboardMetricTile
          name={"Open bevindingen"}
          trendArrow={headTiles.openFindings.trend}
          inverseTrendArrowStyle={true}
          value={headTiles.openFindings.formatCurrent}
          subText={
            headTiles.openFindings.trend === null
              ? "hetzelfde t.o.v. vorige periode"
              : `${Math.abs(headTiles.openFindings.delta).toFixed(0)} ${headTiles.inspectionsCount.trend === "up" ? "meer" : "minder"} t.o.v. vorige periode`
          }
          subTextStyle={
            headTiles.openFindings.trend === null
              ? "amber"
              : headTiles.openFindings.trend === "up"
                ? "red"
                : "green"
          }
        />
        <DashboardMetricTile
          name={"Opgelost binnen 24 uur"}
          trendArrow={headTiles.solveRate.trend}
          value={headTiles.solveRate.formatCurrent}
          subText={
            headTiles.solveRate.trend === null
              ? "hetzelfde t.o.v. vorige periode"
              : `${Math.abs(headTiles.solveRate.delta).toFixed(0)}% ${headTiles.solveRate.trend === "up" ? "meer" : "minder"} t.o.v. vorige periode`
          }
          subTextStyle={
            headTiles.solveRate.trend === null
              ? "amber"
              : headTiles.solveRate.trend === "up"
                ? "green"
                : "red"
          }
        />
      </section>
      <section className={"grid grid-cols-1 xl:grid-cols-5 gap-4"}>
        <div className={"col-span-2 flex flex-col space-y-4"}>
          {props.projectsInCompany.length > 1 && (
            <DashboardCard title={"Score per project"}>
              <BarGraph
                data={buildAverageScoreBarData(
                  props.projectsInCompany,
                  (project) => props.inspections.filter((i) => i.projectId === project.id),
                  (project) => project.name,
                )}
                styleConditions={SCORE_BAR_STYLE_CONDITIONS}
                max={10}
              />
            </DashboardCard>
          )}
          {subCompanyIds.length > 1 && (
            <DashboardCard title={"Score per subbedrijf"}>
              <BarGraph
                data={buildAverageScoreBarData(
                  subCompanyIds,
                  (companyId) => props.inspections.filter((i) => i.companyId === companyId),
                  (companyId) => props.companies.find((c) => c.id === companyId)?.name ?? companyId,
                )}
                styleConditions={SCORE_BAR_STYLE_CONDITIONS}
                max={10}
              />
            </DashboardCard>
          )}
          {hijsfirmas.length > 1 && (
            <DashboardCard title={"Score per hijsbedrijf"}>
              <BarGraph
                data={buildAverageScoreBarData(
                  hijsfirmas,
                  (h) =>
                    props.inspections.filter((i) => getHijsfirmas(i).some((f) => f.key === h.key)),
                  (h) => h.label,
                )}
                styleConditions={SCORE_BAR_STYLE_CONDITIONS}
                max={10}
              />
            </DashboardCard>
          )}
        </div>

        <div className={"col-span-3 flex flex-col space-y-4"}>
          <DashboardCard title={"Score per categorie"}>
            <div className={"grid grid-cols-1 xl:grid-cols-4 gap-4"}>
              <DashboardMetricTile
                name={"Basis kennis"}
                value={categoryTiles.knowledge.formatCurrent}
                trendArrow={categoryTiles.knowledge.trend}
                barGraphValue={categoryTiles.knowledge.current / 100}
                barGraphStyle={
                  categoryTiles.knowledge.current < 55
                    ? "red"
                    : categoryTiles.knowledge.current >= 70
                      ? "green"
                      : "amber"
                }
              />
              <DashboardMetricTile
                name={"Expertise"}
                value={categoryTiles.expertise.formatCurrent}
                trendArrow={categoryTiles.expertise.trend}
                barGraphValue={categoryTiles.expertise.current / 100}
                barGraphStyle={
                  categoryTiles.expertise.current < 55
                    ? "red"
                    : categoryTiles.expertise.current >= 70
                      ? "green"
                      : "amber"
                }
              />
              <DashboardMetricTile
                name={"Gedrag"}
                value={categoryTiles.behavior.formatCurrent}
                trendArrow={categoryTiles.behavior.trend}
                barGraphValue={categoryTiles.behavior.current / 100}
                barGraphStyle={
                  categoryTiles.behavior.current < 55
                    ? "red"
                    : categoryTiles.behavior.current >= 70
                      ? "green"
                      : "amber"
                }
              />
              <DashboardMetricTile
                name={"Veiligheid"}
                value={categoryTiles.safety.formatCurrent}
                trendArrow={categoryTiles.safety.trend}
                barGraphValue={categoryTiles.safety.current / 100}
                barGraphStyle={
                  categoryTiles.safety.current < 55
                    ? "red"
                    : categoryTiles.safety.current >= 70
                      ? "green"
                      : "amber"
                }
              />
            </div>
          </DashboardCard>
          <DashboardCard title={"Oplosstatus kritieke bevindingen per project"}>
            <BarGraph
              data={[
                ...props.projectsInCompany
                  .map((project) => {
                    const projectInspections = props.inspections.filter(
                      (i) => i.projectId === project.id,
                    );
                    if (projectInspections.length === 0) return null;
                    const averageProjectScore =
                      projectInspections.reduce(
                        (c, i) => c + (i?.cachedResults?.critical_findings_fixed ?? 0),
                        0,
                      ) /
                      Math.max(
                        1,
                        projectInspections.reduce(
                          (c, i) => c + (i?.cachedResults?.critical_findings_total ?? 0),
                          0,
                        ),
                      );
                    return {
                      label: project.name,
                      value: averageProjectScore,
                      displayAs: `${(averageProjectScore * 100).toFixed(0)}%`,
                    };
                  })
                  .filter((x) => x !== null),
              ]}
              styleConditions={SCORE_BAR_STYLE_CONDITIONS}
              max={1}
            />
          </DashboardCard>
        </div>
      </section>
      <section className={"grid grid-cols-1 xl:grid-cols-5 gap-4"}>
        <div className={"col-span-3 flex flex-col space-y-4"}>
          <DashboardCard title={"Top 3 stijgende bevindingen"}>
            {findingsTrend.risingFindingQuestionIds.length === 0 ? (
              <div className={"px-3 text-slate-600"}>Geen</div>
            ) : (
              <table className={"w-full"}>
                <tbody>
                  {findingsTrend.risingFindingQuestionIds.map((entry, i) => {
                    const question = props.questions.find((q) => q.id === entry.question);
                    return (
                      <tr key={i}>
                        <td className={"py-1"}>
                          <InspectionFindingSeverityBadge severity={"critical"} />
                        </td>
                        <td className={"px-3 py-1 text-sm text-slate-700"}>
                          {questionLabel(question)}
                        </td>
                        <td className={"py-1 pr-2"}>
                          <TrendArrow trend={"up"} trendStyle={"red"} />
                        </td>
                        <td className={"py-1 font-medium"}>
                          {entry.currentCount}&nbsp;&nbsp;
                          <span className="text-xs font-base text-red-800">
                            +{(entry.deltaPercent * 100).toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </DashboardCard>
          <DashboardCard title={"Top 3 dalende bevindingen"}>
            {findingsTrend.fallingFindingQuestionIds.length === 0 ? (
              <div className={"px-3 text-slate-600"}>Geen</div>
            ) : (
              <table className={"w-full"}>
                <tbody>
                  {findingsTrend.fallingFindingQuestionIds.map((entry, i) => {
                    const question = props.questions.find((q) => q.id === entry.question);
                    return (
                      <tr key={i}>
                        <td className={"py-1"}>
                          <InspectionFindingSeverityBadge severity={"critical"} />
                        </td>
                        <td className={"px-3 py-1 text-sm text-slate-700"}>
                          {questionLabel(question)}
                        </td>
                        <td className={"py-1 pr-2"}>
                          <TrendArrow trend={"down"} trendStyle={"green"} />
                        </td>
                        <td className={"py-1 font-medium"}>
                          {entry.currentCount}&nbsp;&nbsp;
                          <span className="text-xs font-base text-green-800">
                            {(entry.deltaPercent * 100).toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </DashboardCard>
        </div>

        <div className={"col-span-2 flex flex-col space-y-4"}>
          <DashboardCard title={"Top 3 positieve items"}>
            {positiveQuestions.length === 0 ? (
              <div className={"px-3 text-slate-600"}>Geen</div>
            ) : (
              <table className={"w-full"}>
                <tbody>
                  {positiveQuestions.map((entry) => {
                    const question = props.questions.find((q) => q.id === entry.question);
                    return (
                      <tr key={entry.question}>
                        <td className={"px-3 py-1 text-sm text-slate-700"}>
                          {questionLabel(question)}
                        </td>
                        <td className={"py-1 pr-2"}>
                          <TrendArrow
                            trend={entry.delta > 0 ? "up" : entry.delta < 0 ? "down" : "flat"}
                            trendStyle={
                              entry.delta > 0 ? "red" : entry.delta < 0 ? "green" : "slate"
                            }
                          />
                        </td>
                        <td className={"py-1 font-medium"}>
                          {entry.currentCount}&nbsp;&nbsp;
                          <span
                            className={`text-xs font-base ${entry.delta > 0 ? "text-red-800" : entry.delta < 0 ? "text-green-800" : "text-slate-500"}`}
                          >
                            {entry.delta > 0 ? "+" : ""}
                            {(entry.deltaPercent * 100).toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </DashboardCard>
        </div>
      </section>
    </div>
  );
};
